/**
 * Fotos e videos que esperam para subir ao Storage.
 *
 * O arquivo NUNCA passa pelo outbox do sync: um video nao pode travar a fila
 * das medidas do dia. A linha de `media` (metadados, sha256) vai pelo sync como
 * qualquer outra; o binario fica aqui, no aparelho, ate subir. Quando sobe, o
 * `storage_path` vai pelo sync (escrita parcial) e o binario local e apagado,
 * para o tablet nao encher depois de quatro dias de haras.
 */

/** Limite por arquivo do plano gratuito do Supabase. O bucket recusa acima disso. */
export const LIMITE_BYTES = 50 * 1024 * 1024;

export interface ArquivoLocal {
  mediaId: string;
  assessmentId: string;
  blob: Blob;
  mime: string;
  tamanho: number;
  estado: 'pendente' | 'erro';
  erro: string | null;
  criadoEm: number;
}

/** Onde os arquivos esperam no aparelho. */
export interface ArquivosStore {
  guardar(a: ArquivoLocal): Promise<void>;
  /** Pendentes e com erro, do mais antigo para o mais novo. */
  pendentes(): Promise<ArquivoLocal[]>;
  obter(mediaId: string): Promise<ArquivoLocal | undefined>;
  marcarErro(mediaId: string, erro: string): Promise<void>;
  apagar(mediaId: string): Promise<void>;
}

/** Implementacao de referencia, para os testes. */
export class MemoryArquivos implements ArquivosStore {
  readonly arquivos = new Map<string, ArquivoLocal>();
  async guardar(a: ArquivoLocal) {
    this.arquivos.set(a.mediaId, { ...a });
  }
  async pendentes() {
    return [...this.arquivos.values()].sort((a, b) => a.criadoEm - b.criadoEm);
  }
  async obter(mediaId: string) {
    return this.arquivos.get(mediaId);
  }
  async marcarErro(mediaId: string, erro: string) {
    const a = this.arquivos.get(mediaId);
    if (a) this.arquivos.set(mediaId, { ...a, estado: 'erro', erro });
  }
  async apagar(mediaId: string) {
    this.arquivos.delete(mediaId);
  }
}

const EXTENSAO: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/heic': 'heic',
  'image/heif': 'heif',
  'image/webp': 'webp',
  'video/mp4': 'mp4',
  'video/quicktime': 'mov',
  'video/webm': 'webm',
};

/**
 * `{assessment_id}/{media_id}.{ext}`: o primeiro segmento e a ficha, e e por ele
 * que o Storage decide quem pode ler (ver 0009).
 */
export function caminhoDoArquivo(assessmentId: string, mediaId: string, mime: string): string {
  const ext = EXTENSAO[mime] ?? mime.split('/')[1]?.replace(/[^a-z0-9]/gi, '') ?? 'bin';
  return `${assessmentId}/${mediaId}.${ext || 'bin'}`;
}

/** Recusa o que o bucket recusaria, antes de gravar no aparelho. `null` = pode seguir. */
export function validarArquivo(
  arquivo: { size: number; type: string },
  tipo: 'foto' | 'video',
): string | null {
  if (!arquivo.type.startsWith(tipo === 'foto' ? 'image/' : 'video/')) {
    return tipo === 'foto' ? 'Escolha uma foto (imagem).' : 'Escolha um vídeo.';
  }
  if (arquivo.size > LIMITE_BYTES) {
    const mb = (arquivo.size / 1024 / 1024).toFixed(0);
    return `O arquivo tem ${mb} MB, e o limite é 50 MB. Encurte o vídeo ou reduza a resolução antes de anexar.`;
  }
  return null;
}

/** SHA-256 do arquivo, em hex. Vai na linha de `media`: prova de que o arquivo nao mudou. */
export async function sha256DoBlob(blob: Blob): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export interface ResultadoDoEnvio {
  enviados: number;
  falhas: Array<{ mediaId: string; erro: string }>;
}

/**
 * A fila de envio. `enviar` sobe o binario (Supabase Storage no app, um falso
 * nos testes); `registrar` grava o `storage_path` na `media`, pelo sync.
 */
export class EnvioDeArquivos {
  private rodando = false;

  constructor(
    private readonly store: ArquivosStore,
    private readonly enviar: (caminho: string, blob: Blob, mime: string) => Promise<void>,
    private readonly registrar: (mediaId: string, patch: Record<string, unknown>) => Promise<unknown>,
  ) {}

  async enviarPendentes(): Promise<ResultadoDoEnvio> {
    if (this.rodando) return { enviados: 0, falhas: [] };
    this.rodando = true;
    const resultado: ResultadoDoEnvio = { enviados: 0, falhas: [] };
    try {
      for (const a of await this.store.pendentes()) {
        const caminho = caminhoDoArquivo(a.assessmentId, a.mediaId, a.mime);
        try {
          await this.enviar(caminho, a.blob, a.mime);
        } catch (e) {
          const erro = e instanceof Error ? e.message : String(e);
          // Ja existe no Storage: um envio anterior subiu e o app morreu antes de registrar.
          if (!/already exists|Duplicate|409/i.test(erro)) {
            await this.store.marcarErro(a.mediaId, erro);
            resultado.falhas.push({ mediaId: a.mediaId, erro });
            continue;
          }
        }
        await this.registrar(a.mediaId, { storage_path: caminho, uploaded_at: new Date().toISOString() });
        await this.store.apagar(a.mediaId);
        resultado.enviados++;
      }
      return resultado;
    } finally {
      this.rodando = false;
    }
  }
}
